import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Spinner } from "@app/components";
import { getAppPaths } from "@app/routes/app-paths";
import { getEnvConfig, indexByProp } from "@app/utils";
import type { Project, ScenarioInteractive, ScenarioRulesBased } from "@honeycomb/api";
import { useGetMe, useGetPreferences, useUserState } from "@honeycomb/api";
import type { Activity, ActivityDefinitions } from "@honeycomb/mystique";
import { useAuthContext } from "@honeycomb/mystique";
import { ActivityMonitorProvider, useLocalStorage, usePusherContext } from "@honeycomb/mystique";
import { useFlags, useFlagsStatus, useUnleashContext, useVariant } from "@unleash/proxy-client-react";

export type ActivityProject = Activity<
  "project",
  Omit<Project, "algorithmTypes" | "importStatus" | "importResultMeta" | "isArchived" | "isDeleted">
>;
export type ActivityScenarioInteractive = Activity<
  "scenario-interactive",
  Omit<
    ScenarioInteractive,
    | "isArchived"
    | "reviewMeta"
    | "canOptimise"
    | "note"
    | "group"
    | "archivedOn"
    | "archivedBy"
    | "isFavourite"
    | "goal"
    | "isBase"
    | "isCore"
    | "type"
  >
>;
export type ActivityScenarioRule = Activity<
  "scenario-rule",
  Pick<
    ScenarioRulesBased,
    "id" | "name" | "currentWeeklyRevenue" | "createdBy" | "createdOn" | "updatedBy" | "updatedOn"
  >
>;

export const UserLoader = () => {
  const env = getEnvConfig();
  const appPaths = useMemo(() => {
    return getAppPaths();
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useFlags();
  const updateUnleash = useUnleashContext();
  const { flagsReady } = useFlagsStatus();
  const uxTheme = useVariant("ux_theme");
  const [uxMode] = useLocalStorage<string>({
    key: "uxMode",
    defaultValue: "classic",
    onExternalUpdate: () => {
      window.location.reload();
    },
  });
  const [uiTheme, setUiTheme] = useLocalStorage<string>({
    key: "uiTheme",
    defaultValue: "classic",
    onExternalUpdate: () => {
      window.location.reload();
    },
  });
  const { initPusher } = usePusherContext();
  const { getAccessToken, isLoading: isAuthorizing, provider: authProvider } = useAuthContext();
  const { tenant: activeTenant, setUser, token, isLoggedIn, features } = useUserState();
  const [isFetchingUser, userData, userSend, userError] = useGetMe(false);
  const [isFetchingPrefs, prefsData, prefsSend, prefsError] = useGetPreferences(false);
  const hasFailedUserLoad = useMemo(() => {
    return userError !== undefined || prefsError !== undefined;
  }, [prefsError, userError]);
  const [hasAttemptedTokenRetrieval, setHasAttemptedTokenRetrieval] = useState(false);
  const [hasTokenRetrievalSuccess, setHasTokenRetrievalSuccess] = useState(false);
  const [hasTokenRetrievalError, setHasTokenRetrievalError] = useState(false);
  const isTokenRetrievalCompleted = useMemo(() => {
    return hasTokenRetrievalSuccess || hasTokenRetrievalError;
  }, [hasTokenRetrievalError, hasTokenRetrievalSuccess]);
  const [isAuthorizationCompleted, setIsAuthorizationCompleted] = useState(false);
  const activityKinds: ActivityDefinitions = useMemo(() => {
    const activityProject: ActivityProject = {
      kind: "project",
      entity: {
        id: 0,
        name: "",
        updatedOn: "",
        createdOn: "",
        baseWeeklyRevenue: 0,
        currentWeeklyRevenue: 0,
        c13nEnabled: false,
      },
    };
    const activityScenarioInteractive: ActivityScenarioInteractive = {
      kind: "scenario-interactive",
      entity: {
        id: 0,
        name: "",
        currentWeeklyRevenue: 0,
        project: 0,
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
        clonedFromId: 0,
        reviewMetaId: 0,
      },
    };
    const activityScenarioRules: ActivityScenarioRule = {
      kind: "scenario-rule",
      entity: {
        id: 0,
        name: "",
        currentWeeklyRevenue: 0,
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
    };

    return [activityProject, activityScenarioInteractive, activityScenarioRules];
  }, []);

  const toggleStyling = useCallback(() => {
    /**
     * NOTE / REMINDER
     *
     * Once the UI refresh is complete this function will no longer be needed.
     *
     * As more pages are implemented with the new UX, this will need to be updated.
     *
     * This is needed to ensure that the new UX styling is applied correctly, due
     * to antD and the classic styling applying rules to elements directly.
     */
    const route = location.pathname;
    const disableClassicStyling = uxMode !== "classic" && ["/home"].indexOf(route) !== -1;
    const hrefDisableList = ["root", "ui"];

    const checkStyleRef = (ref: string) => {
      let disable = false;

      for (let i = 0, ii = hrefDisableList.length; i < ii; i++) {
        if (ref.indexOf(hrefDisableList[i]) !== -1) {
          disable = true;
          break;
        }
      }

      return disable;
    };

    const disableStyleSheets = () => {
      for (const sheet of document.styleSheets) {
        const node = sheet.ownerNode as HTMLElement;

        if (node.dataset.styledVersion) {
          sheet.disabled = true;
        }

        if (node.dataset.viteDevId && checkStyleRef(node.dataset.viteDevId)) {
          sheet.disabled = true;
        }

        if (sheet.href && checkStyleRef(sheet.href)) {
          sheet.disabled = true;
        }
      }
    };

    const enableStyleSheets = () => {
      for (const sheet of document.styleSheets) {
        sheet.disabled = false;
      }
    };

    if (disableClassicStyling) {
      disableStyleSheets();
      return;
    }

    enableStyleSheets();
  }, [location.pathname, uxMode]);

  // Step 1, try to retrieve user access token
  useEffect(() => {
    toggleStyling();

    (async () => {
      if (isAuthorizing || hasAttemptedTokenRetrieval) return;

      setHasAttemptedTokenRetrieval(true);

      if (authProvider === "auth0" && location.hash) {
        // Auth0 implicit flow returns access_token in the URL hash fragment
        const params = new URLSearchParams(location.hash.replace("#", ""));
        const errorCode = params.get("error") || "";
        const message = params.get("error_description") || "";
        const accessToken = params.get("access_token") || "";

        if (errorCode) {
          console.error(`login failed: ${errorCode} - ${message}`);
          setHasTokenRetrievalError(true);
          return;
        }

        if (accessToken) {
          setUser({ token: accessToken });
          setHasTokenRetrievalSuccess(true);
          return;
        }
      }

      try {
        const jwt = await getAccessToken();

        setUser({ token: jwt });
        setHasTokenRetrievalSuccess(true);
      } catch (e) {
        // this will error when an unauthenticated user needs to login, which is expected behaviour
        console.warn(e);
        setHasTokenRetrievalError(true);
      }
    })();
  }, [
    authProvider,
    env.ENV,
    getAccessToken,
    hasAttemptedTokenRetrieval,
    isAuthorizing,
    location.hash,
    setUser,
    toggleStyling,
  ]);

  // Step 2, fetch user data & preferences and configure pusher
  useEffect(() => {
    if (!hasTokenRetrievalSuccess || !token) return;

    userSend({ params: true });
    prefsSend({ params: true });
  }, [hasTokenRetrievalSuccess, prefsSend, token, userSend]);

  // Step 3, apply user data & preferences, setup pusher, and refresh feature flags
  useEffect(() => {
    if (isLoggedIn || isFetchingUser || isFetchingPrefs || !token) return;

    if (userError || prefsError) {
      if (userError) {
        console.debug("error while fetching user data", userError);
      }

      if (prefsError) {
        console.debug("error while fetching preferences", prefsError);
      }

      return;
    }

    if (userData) {
      setUser({ ...userData.data, isLoggedIn: prefsData !== undefined });
      initPusher(token, userData.data.email);
      updateUnleash({ userId: userData.data.email });
    }

    if (prefsData) {
      setUser({ preferences: prefsData.data, isLoggedIn: userData !== undefined });
    }
  }, [
    initPusher,
    isFetchingPrefs,
    isFetchingUser,
    isLoggedIn,
    prefsData,
    prefsError,
    setUser,
    token,
    updateUnleash,
    userData,
    userError,
  ]);

  // Step 4, configure user feature flags
  useEffect(() => {
    /**
     * Handles feature flags
     * Getting all features flags doe not return the complete list on the first pass,
     * so we need to allow the flags to be updated if there is they are loaded in
     */
    if (!isLoggedIn) return;
    // flags from unleash match current user flags
    if (features && Object.keys(features).length === featureFlags.length) return;

    const flags: Record<string, boolean> = {};

    featureFlags.forEach((feature) => {
      flags[feature.name.toLowerCase()] = feature.enabled;
    });

    setUser({ features: flags });
  }, [featureFlags, features, isLoggedIn, setUiTheme, setUser, uiTheme]);

  useEffect(() => {
    if (!uxTheme || !uxTheme.enabled || !uxTheme.feature_enabled) return;
    if (uxTheme.name !== "themes") return;

    try {
      const themes: Array<string> = JSON.parse(uxTheme.payload?.value || "[]");

      if (!themes.length) {
        console.warn("theme feature has no values", uxTheme);
        return;
      }

      const idx = themes.indexOf(uiTheme || "classic");

      if (idx == -1) {
        console.warn(`unable to deteremine user theme (${uiTheme}): defaulting to classic`, uxTheme);
        setUiTheme("classic");
        return;
      }

      if (themes[idx] === uiTheme) {
        return;
      }

      setUiTheme(themes[idx]);
    } catch (e) {
      console.error("failed to parse theme feature", e);
    }
  }, [setUiTheme, uiTheme, uxTheme]);

  // Step 5, determine where to send user
  useEffect(() => {
    if (isAuthorizing) return; // auth provider and featureFlags still loading
    if (!isTokenRetrievalCompleted) return; // still getting token from auth provider
    if (hasTokenRetrievalSuccess && !isLoggedIn) return; // have token, loading user
    if (hasTokenRetrievalError || hasFailedUserLoad) {
      // TODO ::: create an error page for this state instead of sending user back to login page
      navigate(`/${appPaths.login}`);
      setIsAuthorizationCompleted(true);
      return;
    }
    if (!flagsReady || featureFlags.length <= 1) return; // wait until user has there feature flags loaded

    if (location.pathname === `/${appPaths.login}` || location.pathname === `/`) {
      console.log("logging in");
      // redirect authenicated user to the home page
      const idx = indexByProp(featureFlags, "name", "mobile_client");
      console.log(`flag index: ${idx}`);
      if (idx === -1 || !featureFlags[idx].enabled) {
        navigate(`/${appPaths.home}`);
        console.log("routing to desktop");
        return;
      }

      console.log("routing to mobile");
      navigate(`/mobile/stores`);
    }

    setIsAuthorizationCompleted(true);
  }, [
    appPaths.home,
    appPaths.login,
    featureFlags,
    featureFlags.length,
    flagsReady,
    hasFailedUserLoad,
    hasTokenRetrievalError,
    hasTokenRetrievalSuccess,
    isAuthorizing,
    isLoggedIn,
    isTokenRetrievalCompleted,
    location.pathname,
    navigate,
  ]);

  if (!isAuthorizationCompleted) {
    return <Spinner height={"100vh"} />;
  }

  return (
    <ActivityMonitorProvider partitionKey={activeTenant} activityKinds={activityKinds}>
      <Outlet />
    </ActivityMonitorProvider>
  );
};
